import { DndContext, type DragEndEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, ButtonBase, IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';

import { ElevatorNames } from '~/components/tasks/ElevatorNames';
import {
  type StatusGroup,
  useStatusGroups,
  useTaskDragSensors,
} from '~/components/tasks/taskGroups';
import { previewRowProps, type TaskHover } from '~/components/tasks/taskPreviewRow';
import {
  avatarColor,
  formatShortDate,
  initials,
  isFollowUpOverdue,
  STATUS_COLOR,
} from '~/components/tasks/taskStyle';
import type { TaskDetail, TaskStatus, TaskSummary } from '~/data/generated/model';
import { SURFACE_RADIUS } from '~/theme';

const MUTED = { fontSize: 13, color: 'text.secondary' } as const;

function columnsFor(showProperty: boolean): string {
  return [
    '32px',
    '76px',
    'minmax(200px, 2fr)',
    'minmax(220px, 2fr)',
    ...(showProperty ? ['minmax(140px, 1fr)'] : []),
    'minmax(120px, 1fr)',
    '160px',
    '96px',
    '96px',
  ].join(' ');
}

const ROW_MIN_WIDTH = (showProperty: boolean) => (showProperty ? 1200 : 1060);

function HeaderRow({ showProperty }: { showProperty: boolean }) {
  const cells = [
    '',
    'ID',
    'Title',
    'AI summary',
    ...(showProperty ? ['Property'] : []),
    'Elevators',
    'Owner',
    'Follow-up',
    'Last update',
  ];
  return (
    <Box
      aria-hidden
      sx={{
        display: 'grid',
        gridTemplateColumns: columnsFor(showProperty),
        gap: '12px',
        px: '12px',
        py: '6px',
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
        minWidth: ROW_MIN_WIDTH(showProperty),
      }}
    >
      {cells.map((cell, i) => (
        <Box key={i}>{cell}</Box>
      ))}
    </Box>
  );
}

function AiSummaryCell({ summary }: { summary: string }) {
  if (!summary) return <Box sx={MUTED}>—</Box>;
  return (
    <Tooltip title={summary} placement="bottom-start">
      <Box
        sx={{
          ...MUTED,
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {summary}
      </Box>
    </Tooltip>
  );
}

function TaskRow({
  task,
  showProperty,
  onOpen,
  onHover,
  highlighted,
}: {
  task: TaskSummary;
  showProperty: boolean;
  onOpen: () => void;
  onHover: TaskHover;
  highlighted: boolean;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } =
    useDraggable({ id: task.id });
  const overdue = isFollowUpOverdue(task);
  return (
    <Box
      ref={setNodeRef}
      data-testid="task-row"
      {...previewRowProps(task.id, onHover)}
      sx={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 2 : 'auto',
        position: 'relative',
        borderTop: '1px solid #eef0f3',
        bgcolor: 'background.paper',
      }}
    >
      <ButtonBase
        component="div"
        aria-label={`${task.displayId} ${task.title}`}
        onClick={onOpen}
        sx={{
          display: 'grid',
          gridTemplateColumns: columnsFor(showProperty),
          gap: '12px',
          alignItems: 'center',
          width: '100%',
          minWidth: ROW_MIN_WIDTH(showProperty),
          px: '12px',
          py: '8px',
          textAlign: 'left',
          font: 'inherit',
          bgcolor: highlighted ? '#eef3fb' : undefined,
          '&:hover': { bgcolor: highlighted ? '#eef3fb' : '#f6f8fb' },
        }}
      >
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          aria-label={`Move ${task.displayId}`}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          sx={{ cursor: 'grab', touchAction: 'none', color: 'text.secondary' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
        <Box sx={{ font: "12px 'Roboto Mono', monospace", color: '#777' }}>{task.displayId}</Box>
        <Box
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: '#1b1f24',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {task.title}
        </Box>
        <AiSummaryCell summary={task.summary} />
        {showProperty ? (
          <Box
            sx={{ ...MUTED, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {task.propertyName}
          </Box>
        ) : null}
        <Box sx={{ ...MUTED, display: 'flex', minWidth: 0 }}>
          <ElevatorNames cars={task.cars} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              flex: 'none',
              borderRadius: '50%',
              bgcolor: avatarColor(task.assignee?.id),
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {initials(task.assignee?.name)}
          </Box>
          <Box
            sx={{
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.assignee?.name ?? 'Unassigned'}
          </Box>
        </Box>
        <Box
          sx={{
            fontSize: 13,
            color: overdue ? '#c62828' : 'text.secondary',
            fontWeight: overdue ? 500 : 400,
          }}
        >
          {task.followUpDate ? formatShortDate(task.followUpDate) : '—'}
        </Box>
        <Box sx={MUTED}>
          {task.latestUpdate ? formatShortDate(task.latestUpdate.createdAt) : '—'}
        </Box>
      </ButtonBase>
    </Box>
  );
}

function Section({
  group,
  collapsed,
  onToggle,
  showProperty,
  onOpen,
  onHover,
  highlightedId,
}: {
  group: StatusGroup;
  collapsed: boolean;
  onToggle: () => void;
  showProperty: boolean;
  onOpen: (task: TaskSummary) => void;
  onHover: TaskHover;
  highlightedId: string | null;
}) {
  const { status, shown } = group;
  const { setNodeRef, isOver } = useDroppable({ id: status.id });
  const color = STATUS_COLOR[status.color];
  return (
    <Box
      ref={setNodeRef}
      role="region"
      aria-label={`${status.name} group`}
      sx={{
        borderRadius: `${SURFACE_RADIUS}px`,
        border: '1px solid',
        borderColor: isOver ? '#9fb8e8' : 'divider',
        bgcolor: isOver ? '#f1f5fd' : 'background.paper',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={onToggle}
        aria-expanded={!collapsed}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          justifyContent: 'flex-start',
          px: '12px',
          py: '10px',
          bgcolor: '#f7f9fc',
          font: 'inherit',
        }}
      >
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transform: collapsed ? 'rotate(-90deg)' : 'none',
            transition: 'transform .15s',
          }}
        />
        <Box
          aria-hidden
          sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color.solid }}
        />
        <Box component="h3" sx={{ m: 0, fontSize: 14, fontWeight: 600 }}>
          {status.name} · {shown.length}
        </Box>
      </ButtonBase>
      {collapsed ? null : (
        <Box sx={{ overflowX: 'auto' }}>
          {shown.length > 0 ? <HeaderRow showProperty={showProperty} /> : null}
          {shown.map((task) => (
            <TaskRow
              key={task.id}
              task={task}
              showProperty={showProperty}
              onOpen={() => onOpen(task)}
              onHover={onHover}
              highlighted={task.id === highlightedId}
            />
          ))}
          {shown.length === 0 ? (
            <Box sx={{ ...MUTED, px: '16px', py: '12px', borderTop: '1px solid #eef0f3' }}>
              No tasks. Drag one here to move it.
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}

export function TaskGroupedList({
  tasks,
  statuses,
  showProperty,
  onOpen,
  onHover,
  highlightedId = null,
  onCompleted,
}: {
  tasks: TaskSummary[];
  statuses: readonly TaskStatus[];
  showProperty: boolean;
  onOpen: (task: TaskSummary) => void;
  onHover?: TaskHover;
  highlightedId?: string | null;
  onCompleted?: (task: TaskDetail) => void;
}) {
  const { groups, move, moveFailed, dismissMoveFailed } = useStatusGroups(
    tasks,
    statuses,
    onCompleted,
  );
  const sensors = useTaskDragSensors();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) void move(String(active.id), String(over.id));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: 0 }}>
      {moveFailed ? (
        <Alert severity="error" onClose={dismissMoveFailed}>
          Couldn&apos;t move the task. Try again.
        </Alert>
      ) : null}
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        {groups.map((group) => (
          <Section
            key={group.status.id}
            group={group}
            collapsed={collapsed.has(group.status.id)}
            onToggle={() =>
              setCollapsed((prev) => {
                const next = new Set(prev);
                if (next.has(group.status.id)) next.delete(group.status.id);
                else next.add(group.status.id);
                return next;
              })
            }
            showProperty={showProperty}
            onOpen={onOpen}
            onHover={onHover}
            highlightedId={highlightedId}
          />
        ))}
      </DndContext>
    </Box>
  );
}
